"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, type Ref, createContext, use } from "react";
import { Radio as AriaRadio, RadioGroup as AriaRadioGroup, type RadioGroupProps as AriaRadioGroupProps, type RadioProps as AriaRadioProps } from "react-aria-components";
import { cx } from "@/utils/cx";
export interface RadioGroupContextType {
  size?: "sm" | "md";
}
const RadioGroupContext = createContext<RadioGroupContextType | null>(null);
export interface RadioButtonBaseProps {
  size?: "sm" | "md";
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
}
export const RadioButtonBase = t0 => {
  const $ = _c(15);
  const {
    className,
    isFocusVisible,
    isSelected,
    isDisabled,
    size: t1
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const t2 = size === "md" && "size-5 min-h-5 min-w-5";
  const t3 = isSelected && !isDisabled && "bg-brand-solid ring-bg-brand-solid";
  const t4 = isDisabled && "cursor-not-allowed border-disabled bg-disabled_subtle";
  const t5 = isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring";
  let t6;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = cx("flex size-4 min-h-4 min-w-4 cursor-pointer appearance-none items-center justify-center rounded-full bg-primary ring-1 ring-primary ring-inset", t2, t3, t4, t5, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = size === "md" && "size-2";
  const t8 = isDisabled && "bg-fg-disabled_subtle";
  const t9 = isSelected && "opacity-100";
  let t10;
  if ($[6] !== t7 || $[7] !== t8 || $[8] !== t9) {
    t10 = cx("size-1.5 rounded-full bg-fg-white opacity-0 transition-inherit-all", t7, t8, t9);
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10) {
    t11 = <div className={t10} />;
    $[10] = t10;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11 || $[13] !== t6) {
    t12 = <div className={t6}>{t11}</div>;
    $[12] = t11;
    $[13] = t6;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  return t12;
};
RadioButtonBase.displayName = "RadioButtonBase";
interface RadioButtonProps extends AriaRadioProps {
  size?: "sm" | "md";
  label?: ReactNode;
  hint?: ReactNode;
  ref?: Ref<HTMLLabelElement>;
}
export const RadioButton = t0 => {
  const $ = _c(18);
  let ariaRadioProps;
  let className;
  let hint;
  let label;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      hint,
      className,
      size: t1,
      ...ariaRadioProps
    } = t0);
    $[0] = t0;
    $[1] = ariaRadioProps;
    $[2] = className;
    $[3] = hint;
    $[4] = label;
    $[5] = t1;
  } else {
    ariaRadioProps = $[1];
    className = $[2];
    hint = $[3];
    label = $[4];
    t1 = $[5];
  }
  let size = t1 === undefined ? "sm" : t1;
  const context = use(RadioGroupContext);
  size = context?.size ?? size;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sm: {
        root: "gap-2",
        textWrapper: "",
        label: "text-sm font-medium",
        hint: "text-sm"
      },
      md: {
        root: "gap-3",
        textWrapper: "gap-0.5",
        label: "text-md font-medium",
        hint: "text-md"
      }
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const sizes = t2;
  let t3;
  if ($[7] !== className || $[8] !== size) {
    t3 = renderProps => cx("flex items-start", renderProps.isDisabled && "cursor-not-allowed", sizes[size].root, typeof className === "function" ? className(renderProps) : className);
    $[7] = className;
    $[8] = size;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== hint || $[11] !== label || $[12] !== size) {
    t4 = t5 => {
      const {
        isSelected,
        isDisabled,
        isFocusVisible
      } = t5;
      return <><RadioButtonBase size={size} isSelected={isSelected} isDisabled={isDisabled} isFocusVisible={isFocusVisible} className={label || hint ? "mt-0.5" : ""} />{(label || hint) && <div className={cx("inline-flex flex-col", sizes[size].textWrapper)}>{label && <p className={cx("text-secondary select-none", sizes[size].label)}>{label}</p>}{hint && <span role="button" tabIndex={0} className={cx("text-tertiary", sizes[size].hint)} onClick={_temp} onKeyDown={_temp2}>{hint}</span>}</div>}</>;
    };
    $[10] = hint;
    $[11] = label;
    $[12] = size;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== ariaRadioProps || $[15] !== t3 || $[16] !== t4) {
    t5 = <AriaRadio {...ariaRadioProps} className={t3}>{t4}</AriaRadio>;
    $[14] = ariaRadioProps;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
RadioButton.displayName = "RadioButton";
interface RadioGroupProps extends RadioGroupContextType, AriaRadioGroupProps {
  children: ReactNode;
  className?: string;
}
export const RadioGroup = t0 => {
  const $ = _c(16);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "sm" : t1;
  let t2;
  if ($[5] !== size) {
    t2 = {
      size
    };
    $[5] = size;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cx("flex flex-col gap-4", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t3) {
    t4 = <AriaRadioGroup {...props} className={t3}>{children}</AriaRadioGroup>;
    $[9] = children;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <RadioGroupContext.Provider value={t2}>{t4}</RadioGroupContext.Provider>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}